import {GridModel} from '@xh/hoist/cmp/grid';
import {LoadSpec, XH, managed} from '@xh/hoist/core';
import {HoistModel, TaskObserver} from '@xh/hoist/core';
import {actionCol, calcActionColWidth} from '@xh/hoist/desktop/cmp/grid';
import {Icon} from '@xh/hoist/icon';
import {action, observable} from '@xh/hoist/mobx';
import {asServerException} from '../../../../../core/ServiceUtils';
import {FormModel} from '@xh/hoist/cmp/form';
import {required} from '@xh/hoist/data';
import {Security, WatchList} from '../../../../data/DataTypes';
import {GridOptions} from 'ag-grid-community';

export class WatchlistDialogModel extends HoistModel {
    @observable accessor isOpen = false;
    @managed gridModel = this.createGridModel();
    @managed formModel = this.createFormModel();
    apiTask = TaskObserver.trackLast();

    watchListId: number;
    _resolver;

    constructor(watchListId: number) {
        super();

        this.watchListId = watchListId;
        this.loadAsync().linkTo(this.apiTask);
    }

    override async doLoadAsync(loadSpec: LoadSpec): Promise<void> {
        if (this.watchListId === 0) return;

        try {
            const watchList = await XH.watchListService.fetchWatchlistAsync(this.watchListId);
            this.formModel.init({listName: watchList.name});

            const securities = [];
            for (const ticker of watchList.items) {
                securities.push(await XH.secMasterService.getSecurityByTickerAsync(ticker));
            }
            this.gridModel.store.loadData(securities);
        } catch (e) {
            XH.handleException(asServerException(e), {title: 'Error loading watchlist'});
        }
    }

    async querySecurities(query): Promise<Security[]> {
        return XH.secMasterService.searchSecuritiesAsync(query);
    }

    @action
    async editAsync() {
        this.isOpen = true;
        return new Promise(resolve => (this._resolver = resolve));
    }

    @action
    close() {
        this.isOpen = false;
    }

    save() {
        this.saveAsync().linkTo(this.apiTask);
    }

    async addSecurity(security: Security) {
        const existing = this.gridModel.store.records.map(rec => {
            return rec.data.id;
        });
        if (existing.includes(security.id)) return;

        this.gridModel.store.addRecords([security]);
    }

    async removeSecurity(id) {
        this.gridModel.store.removeRecords([id]);
    }

    async saveAsync() {
        const isValid = await this.formModel.validateAsync();
        if (!isValid) return false;

        const {listName} = this.formModel.getData(),
            tickers = [];
        this.gridModel.agApi.forEachNode(node => {
            tickers.push(node.data.data.ticker);
        });

        const watchList: WatchList = {
            id: this.watchListId,
            name: listName,
            items: tickers
        };

        console.log('Saving watchlist', watchList);

        try {
            await XH.watchListService.saveWatchlistAsync(watchList);

            this._resolver();
            this.close();
        } catch (e) {
            XH.handleException(asServerException(e), {title: 'Error saving watchlist'});
        }
    }

    get agOptions(): GridOptions {
        return {
            rowDragManaged: true,
            //rowDragEntireRow: true,
            onRowDragEnd: e => this.onRowDragged(e)
        };
    }

    private onRowDragged(e) {}

    private createGridModel(): GridModel {
        return new GridModel({
            hideHeaders: true,
            emptyText: 'No tickers',
            columns: [
                {field: 'id', hidden: true},
                {
                    field: 'ticker',
                    width: 105,
                    agOptions: {rowDrag: true},
                    cellClass: 'watchlist-dialog__item-ticker'
                },
                {
                    field: 'name',
                    flex: true,
                    cellClass: 'watchlist-dialog__item-name'
                },
                {
                    ...actionCol,
                    actionsShowOnHoverOnly: true,
                    width: calcActionColWidth(1),
                    actions: [
                        {
                            icon: Icon.delete(),

                            intent: 'danger',
                            actionFn: ({record}) => this.removeSecurity(record.data.id)
                        }
                    ]
                }
            ]
        });
    }

    private createFormModel(): FormModel {
        return new FormModel({
            fields: [
                {
                    name: 'listName',
                    rules: [required]
                }
            ]
        });
    }
}
